import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  JOB_FIGURE_DESCRIPTIONS,
  type JobFigure,
  type JobOptions,
  type JobPeopleRequest,
  type JobResponse,
  PROJECT_FIGURES,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { apiRequest } from '../../lib/api.js';
import { FIGURE_ORDER, figureLabel, jobKeys } from './jobs-ui.js';

interface Row {
  userId: string;
  figure: JobFigure;
  canSee: boolean;
}

/**
 * PM, Account and Commerciale of a commessa (16/10/2026): one each for now,
 * more allowed. Only users with the module Commesse e progetti.
 */
export function JobPeopleDialog({ job, onClose }: { job: JobResponse; onClose: () => void }) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [rows, setRows] = useState<Row[]>(
    job.people.map(({ userId, figure, canSee }) => ({ userId, figure, canSee })),
  );
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const project = job.kind === 'progetto';
  // The PM of a progetto may be any active user (26/10/2026).
  const options = useQuery({
    queryKey: [...jobKeys.options, job.kind],
    queryFn: () => apiRequest<JobOptions>(`/jobs/options?kind=${job.kind}`),
  });
  const figures = project ? PROJECT_FIGURES : FIGURE_ORDER;
  // Who holds a figure stays in the choice even if they lost the module since.
  const users = [
    ...(options.data?.users ?? []),
    ...job.people
      .filter((person) => !options.data?.users.some((user) => user.id === person.userId))
      .map((person) => ({ id: person.userId, name: person.name, email: person.email })),
  ].filter((user, index, all) => all.findIndex((other) => other.id === user.id) === index);
  const save = useMutation({
    mutationFn: (body: JobPeopleRequest) =>
      apiRequest<JobResponse>(`/jobs/${job.id}/people`, { method: 'PUT', body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: jobKeys.all });
      onClose();
    },
  });
  const update = (index: number, change: Partial<Row>) =>
    setRows(rows.map((row, at) => (at === index ? { ...row, ...change } : row)));
  const complete = rows.every((row) => row.userId);

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>Figure di {job.code}</h2>
        <p className="muted small">
          {project
            ? 'Il PM vede e aggiorna il progetto, anche senza il modulo né l’area: può essere qualunque utente attivo.'
            : 'Le figure vedono la commessa, non la modificano: la modifica dipende dai permessi del modulo. Solo utenti con il modulo Commesse e progetti.'}
        </p>
        {figures.map((figure) => (
          <section key={figure} className="stack tight">
            <div className="row between">
              <h3>{figureLabel(figure)}</h3>
              <button
                type="button"
                className="link"
                onClick={() => setRows([...rows, { userId: '', figure, canSee: false }])}
              >
                + {figureLabel(figure)}
              </button>
            </div>
            {!project && <small className="muted">{JOB_FIGURE_DESCRIPTIONS[figure]}</small>}
            {rows.map((row, index) =>
              row.figure !== figure ? null : (
                <div key={index} className="row">
                  <select
                    style={{ flex: 1, width: 'auto' }}
                    value={row.userId}
                    onChange={(event) => update(index, { userId: event.target.value })}
                    aria-label={figureLabel(figure)}
                  >
                    <option value="">Scegli…</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                  {figure === 'commerciale' && (
                    <label className="checkbox small">
                      <input
                        type="checkbox"
                        checked={row.canSee}
                        onChange={(event) => update(index, { canSee: event.target.checked })}
                      />{' '}
                      vede la commessa
                    </label>
                  )}
                  <button
                    type="button"
                    className="link"
                    onClick={() => setRows(rows.filter((_, at) => at !== index))}
                  >
                    Togli
                  </button>
                </div>
              ),
            )}
          </section>
        ))}
        {save.isError && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={!complete || save.isPending}
            onClick={() => save.mutate({ people: rows })}
          >
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
